<template>
  <div class="space-y-4">

    <!-- Empty state: module disabled -->
    <div v-if="!loading && !moduleEnabled" class="flex flex-col items-center justify-center py-20">
      <div class="text-5xl mb-4 opacity-40">&#9729;&#65039;</div>
      <p class="text-gray-400 text-sm mb-2">Modulo Clima desativado.</p>
      <p class="text-gray-500 text-xs mb-4">Ative nas Configuracoes para comecar a operar mercados de clima.</p>
      <router-link to="/settings"
        class="px-4 py-2 bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 text-xs rounded-lg hover:bg-cyan-500/30 transition">
        Ir para Configuracoes
      </router-link>
    </div>

    <template v-if="moduleEnabled || loading">

      <!-- ROW 1: Metrics (6 cards) -->
      <div class="grid grid-cols-3 lg:grid-cols-6 gap-2">
        <!-- Saldo (weather budget) -->
        <div class="bg-gradient-to-br from-cyan-600/20 to-cyan-800/10 rounded-xl p-3 border border-cyan-500/20">
          <p class="text-cyan-300 text-[10px] font-medium">Investido</p>
          <p class="text-lg font-bold text-white">{{ formatCurrency(totalInvestedActive) }}</p>
          <p class="text-[10px] text-cyan-400">{{ activeTrades.length }} apostas ativas</p>
        </div>
        <!-- Lucro (P&L weather) -->
        <div class="rounded-xl p-3 border"
          :class="totalPnl >= 0
            ? 'bg-gradient-to-br from-teal-600/20 to-teal-800/10 border-teal-500/20'
            : 'bg-gradient-to-br from-orange-600/20 to-orange-800/10 border-orange-500/20'">
          <p class="text-[10px] font-medium" :class="totalPnl >= 0 ? 'text-teal-300' : 'text-orange-300'">Lucro</p>
          <p class="text-lg font-bold text-white">{{ totalPnl >= 0 ? '+' : '' }}{{ formatCurrency(totalPnl) }}</p>
          <p class="text-[10px] font-semibold" :class="totalPnl >= 0 ? 'text-teal-400' : 'text-orange-400'">
            {{ roi.toFixed(1) }}% ROI
          </p>
        </div>
        <!-- Win Rate -->
        <div class="bg-gradient-to-br from-purple-600/20 to-purple-800/10 rounded-xl p-3 border border-purple-500/20">
          <p class="text-purple-300 text-[10px] font-medium">Win Rate</p>
          <p class="text-lg font-bold text-white">{{ winRate.toFixed(0) }}%</p>
          <div class="mt-1 w-full bg-gray-700/50 rounded-full h-1">
            <div class="h-1 rounded-full transition-all duration-1000"
              :class="winRate >= 60 ? 'bg-green-500' : winRate >= 40 ? 'bg-yellow-500' : 'bg-red-500'"
              :style="{ width: winRate + '%' }"></div>
          </div>
        </div>
        <!-- Trades (wins/losses) -->
        <div class="bg-gray-800/40 rounded-xl p-3 border border-gray-700/30 text-center">
          <p class="text-gray-400 text-[10px]">Trades</p>
          <p class="text-lg font-bold text-white">{{ resolvedTrades.length }}</p>
          <p class="text-[10px]">
            <span class="text-green-400">{{ wins }}</span>
            <span class="text-gray-600"> / </span>
            <span class="text-red-400">{{ losses }}</span>
          </p>
        </div>
        <!-- Apostas Ativas -->
        <div class="bg-gradient-to-br from-sky-600/20 to-sky-800/10 rounded-xl p-3 border border-sky-500/20">
          <p class="text-sky-300 text-[10px] font-medium">Apostas Ativas</p>
          <p class="text-lg font-bold text-white">{{ activeTrades.length }}</p>
          <p class="text-[10px] text-sky-400">{{ formatCurrency(totalInvestedActive) }} inv.</p>
        </div>
        <!-- Previsoes cached -->
        <div class="bg-gradient-to-br from-indigo-600/20 to-indigo-800/10 rounded-xl p-3 border border-indigo-500/20">
          <p class="text-indigo-300 text-[10px] font-medium">Previsoes</p>
          <p class="text-lg font-bold text-white">{{ forecasts.length }}</p>
          <p class="text-[10px] text-indigo-400">cidades monitoradas</p>
        </div>
      </div>

      <!-- ROW 2: P&L Chart -->
      <div class="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div class="px-4 py-2.5 border-b border-gray-800 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-sm">&#128200;</span>
            <h3 class="text-xs font-semibold text-white">P&L Weather</h3>
            <span v-if="chartBalances.length > 0" class="text-[10px] text-gray-500">{{ chartBalances.length }} pontos</span>
          </div>
          <div class="flex items-center gap-3">
            <span v-if="chartBalances.length >= 2"
              class="text-xs font-bold"
              :class="chartPctChange >= 0 ? 'text-teal-400' : 'text-orange-400'">
              {{ chartPctChange >= 0 ? '&#9650;' : '&#9660;' }} {{ Math.abs(chartPctChange).toFixed(2) }}%
            </span>
            <div class="flex gap-1">
              <button v-for="p in [{k:'1d',l:'1D'},{k:'1w',l:'1S'},{k:'1m',l:'1M'}]"
                :key="p.k" @click="setChartPeriod(p.k)"
                class="px-2 py-0.5 rounded text-[10px] font-medium transition"
                :class="chartPeriod === p.k ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-500 hover:text-gray-300'">
                {{ p.l }}
              </button>
            </div>
          </div>
        </div>

        <div class="px-3 pt-3 pb-2">
          <div v-if="chartBalances.length < 2" class="h-[180px] flex items-center justify-center">
            <div class="text-gray-600 text-xs">Sem dados suficientes para o grafico</div>
          </div>
          <div v-else class="relative">
            <!-- Y-axis labels -->
            <div class="absolute left-0 top-0 bottom-7 flex flex-col justify-between pr-1" style="width:48px">
              <span class="text-[9px] text-gray-500 leading-none text-right">{{ formatCurrency(chartYMax) }}</span>
              <span class="text-[9px] text-gray-500 leading-none text-right">{{ formatCurrency(chartYMid) }}</span>
              <span class="text-[9px] text-gray-500 leading-none text-right">{{ formatCurrency(chartYMin) }}</span>
            </div>
            <!-- SVG Chart -->
            <div class="pl-12 relative" @mousemove="onChartHover($event)" @mouseleave="chartTooltip.show = false">
              <svg viewBox="0 0 600 180" preserveAspectRatio="none" class="w-full h-[180px]" style="overflow:visible">
                <defs>
                  <linearGradient id="weatherChartGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" :stop-color="chartColor" stop-opacity="0.25" />
                    <stop offset="100%" :stop-color="chartColor" stop-opacity="0.02" />
                  </linearGradient>
                </defs>
                <!-- Grid lines -->
                <line x1="0" y1="0" x2="600" y2="0" stroke="rgba(55,65,81,0.5)" stroke-width="0.5" />
                <line x1="0" y1="60" x2="600" y2="60" stroke="rgba(55,65,81,0.4)" stroke-width="0.5" stroke-dasharray="4,4" />
                <line x1="0" y1="120" x2="600" y2="120" stroke="rgba(55,65,81,0.4)" stroke-width="0.5" stroke-dasharray="4,4" />
                <line x1="0" y1="180" x2="600" y2="180" stroke="rgba(55,65,81,0.5)" stroke-width="0.5" />
                <line x1="150" y1="0" x2="150" y2="180" stroke="rgba(55,65,81,0.25)" stroke-width="0.5" />
                <line x1="300" y1="0" x2="300" y2="180" stroke="rgba(55,65,81,0.25)" stroke-width="0.5" />
                <line x1="450" y1="0" x2="450" y2="180" stroke="rgba(55,65,81,0.25)" stroke-width="0.5" />
                <!-- Fill area -->
                <path v-if="chartSmoothFillPath" :d="chartSmoothFillPath" fill="url(#weatherChartGrad)" />
                <!-- Smooth line -->
                <path v-if="chartSmoothPath" :d="chartSmoothPath" fill="none" :stroke="chartColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <!-- Hover vertical line -->
                <line v-if="chartTooltip.show" :x1="chartTooltip.svgX" y1="0" :x2="chartTooltip.svgX" y2="180"
                  stroke="rgba(148,163,184,0.35)" stroke-width="1" stroke-dasharray="3,3" />
                <!-- Hover dot -->
                <circle v-if="chartTooltip.show" :cx="chartTooltip.svgX" :cy="chartTooltip.svgY" r="4" :fill="chartColor" stroke="rgba(15,23,42,0.8)" stroke-width="1.5" />
                <!-- Last point pulse -->
                <circle v-if="chartLastPoint && !chartTooltip.show" :cx="chartLastPoint.x" :cy="chartLastPoint.y" r="3" :fill="chartColor" class="animate-chart-pulse" />
                <circle v-if="chartLastPoint && !chartTooltip.show" :cx="chartLastPoint.x" :cy="chartLastPoint.y" r="7" fill="none" :stroke="chartColor" stroke-opacity="0.3" stroke-width="1.5" class="animate-chart-pulse-ring" />
              </svg>
              <!-- Tooltip -->
              <div v-if="chartTooltip.show"
                class="absolute pointer-events-none bottom-full mb-2 text-[10px] px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg shadow-xl transform -translate-x-1/2 whitespace-nowrap z-10"
                :style="{ left: chartTooltip.pct + '%' }">
                <div class="font-bold" :class="chartPctChange >= 0 ? 'text-teal-400' : 'text-orange-400'">{{ formatCurrency(chartTooltip.value) }}</div>
                <div class="text-gray-500 text-[9px]">{{ chartTooltip.date }}</div>
              </div>
            </div>
            <!-- X-axis labels -->
            <div class="pl-12 flex justify-between text-[9px] text-gray-600 mt-1.5">
              <span>{{ chartXStart }}</span>
              <span>{{ chartXMid }}</span>
              <span>{{ chartXEnd }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ROW 3: Desempenho por Agente -->
      <div class="grid gap-2"
        :class="agentStats.length === 1 ? 'grid-cols-1' : agentStats.length === 2 ? 'grid-cols-2' : 'grid-cols-3'"
        v-if="agentStats.length > 0">
        <div v-for="(stat, i) in agentStats" :key="stat.agent.id"
          class="rounded-xl border p-3"
          :class="[
            i === 0 ? 'bg-gradient-to-br from-cyan-900/30 to-cyan-950/20 border-cyan-500/20' :
            i === 1 ? 'bg-gradient-to-br from-teal-900/30 to-teal-950/20 border-teal-500/20' :
                     'bg-gradient-to-br from-sky-900/30 to-sky-950/20 border-sky-500/20'
          ]">
          <div class="flex items-center gap-1.5 mb-1">
            <span class="w-2 h-2 rounded-full shrink-0"
              :class="i === 0 ? 'bg-cyan-500' : i === 1 ? 'bg-teal-500' : 'bg-sky-500'"></span>
            <span class="text-[10px] font-bold uppercase tracking-wider"
              :class="i === 0 ? 'text-cyan-400' : i === 1 ? 'text-teal-400' : 'text-sky-400'">
              {{ stat.agent.name }}
            </span>
            <span class="ml-auto w-1.5 h-1.5 rounded-full" :class="stat.agent.is_active ? 'bg-green-400' : 'bg-gray-600'"></span>
          </div>
          <div class="flex gap-1 flex-wrap mb-2">
            <span v-for="cat in (stat.agent.market_categories || [])" :key="cat"
              class="px-1 py-0.5 bg-gray-700/50 rounded text-gray-400 text-[9px]">{{ cat }}</span>
            <span class="text-[9px] text-gray-600 ml-auto">{{ stat.agent.window_start_minutes }}&ndash;{{ stat.agent.window_end_minutes }}min &middot; conf&ge;{{ stat.agent.min_confidence }}%</span>
          </div>
          <div class="space-y-1">
            <div class="flex justify-between text-[10px]">
              <span class="text-gray-400">Trades</span>
              <span class="text-white font-bold">{{ stat.count }}</span>
            </div>
            <div class="flex justify-between text-[10px]">
              <span class="text-gray-400">W / L</span>
              <span class="font-bold">
                <span class="text-green-400">{{ stat.wins }}</span>
                <span class="text-gray-600"> / </span>
                <span class="text-red-400">{{ stat.losses }}</span>
              </span>
            </div>
            <div class="flex justify-between text-[10px]">
              <span class="text-gray-400">P&L</span>
              <span class="font-bold text-[11px]" :class="stat.pnl >= 0 ? 'text-teal-400' : 'text-orange-400'">
                {{ stat.pnl >= 0 ? '+' : '' }}{{ formatCurrency(stat.pnl) }}
              </span>
            </div>
            <div class="mt-0.5">
              <div class="flex justify-between text-[9px] mb-0.5">
                <span class="text-gray-500">Win Rate</span>
                <span class="font-bold" :class="i === 0 ? 'text-cyan-400' : i === 1 ? 'text-teal-400' : 'text-sky-400'">
                  {{ stat.winRate.toFixed(0) }}%
                </span>
              </div>
              <div class="w-full bg-gray-700/50 rounded-full h-1">
                <div class="h-1 rounded-full transition-all duration-700"
                  :class="i === 0 ? 'bg-cyan-500' : i === 1 ? 'bg-teal-500' : 'bg-sky-500'"
                  :style="{ width: stat.winRate + '%' }"></div>
              </div>
            </div>
            <!-- Subdivisao por risco -->
            <div class="pt-1.5 border-t border-gray-700/40">
              <div class="text-[9px] text-gray-600 mb-1 uppercase tracking-wide">Por Risco</div>
              <div class="space-y-0.5">
                <div v-for="risk in [
                  { label: 'Conserv.', conf: '\u226570%',  color: 'text-cyan-400',  bar: 'bg-cyan-500',  data: stat.risks.conservador },
                  { label: 'Moder.',   conf: '60-69%', color: 'text-teal-400',  bar: 'bg-teal-500',  data: stat.risks.moderado },
                  { label: 'Extremo',  conf: '<60%',   color: 'text-sky-400',   bar: 'bg-sky-500',   data: stat.risks.extremo },
                ]" :key="risk.label" class="flex items-center gap-1 text-[9px]">
                  <span class="text-gray-500 w-[44px] shrink-0">{{ risk.label }}</span>
                  <span class="text-gray-700 w-[30px] shrink-0">{{ risk.conf }}</span>
                  <div class="flex-1 bg-gray-700/40 rounded-full h-0.5">
                    <div class="h-0.5 rounded-full" :class="risk.bar" :style="{ width: risk.data.winRate + '%' }"></div>
                  </div>
                  <span class="text-green-400 shrink-0">{{ risk.data.wins }}</span>
                  <span class="text-gray-700">/</span>
                  <span class="text-red-400 shrink-0">{{ risk.data.count - risk.data.wins }}</span>
                  <span :class="risk.color" class="font-bold w-7 text-right shrink-0">{{ risk.data.winRate }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ROW 3b: Tier Stats fallback (when no agents loaded) -->
      <div class="grid grid-cols-3 gap-2" v-if="agentStats.length === 0 && resolvedTrades.length > 0">
        <div v-for="(tier, i) in tierStats" :key="tier.label"
          class="rounded-xl border p-3"
          :class="[
            i === 0 ? 'bg-gradient-to-br from-teal-900/30 to-teal-950/20 border-teal-500/20' :
            i === 1 ? 'bg-gradient-to-br from-yellow-900/30 to-yellow-950/20 border-yellow-500/20' :
                     'bg-gradient-to-br from-orange-900/30 to-orange-950/20 border-orange-500/20'
          ]">
          <div class="flex items-center gap-1.5 mb-1.5">
            <span class="w-2 h-2 rounded-full shrink-0"
              :class="i === 0 ? 'bg-teal-500' : i === 1 ? 'bg-yellow-500' : 'bg-orange-500'"></span>
            <span class="text-[10px] font-bold uppercase tracking-wider"
              :class="i === 0 ? 'text-teal-400' : i === 1 ? 'text-yellow-400' : 'text-orange-400'">
              {{ tier.label }}
            </span>
          </div>
          <div class="space-y-1">
            <div class="flex justify-between text-[10px]">
              <span class="text-gray-400">Trades</span>
              <span class="text-white font-bold">{{ tier.count }}</span>
            </div>
            <div class="flex justify-between text-[10px]">
              <span class="text-gray-400">W / L</span>
              <span class="font-bold">
                <span class="text-green-400">{{ tier.wins }}</span>
                <span class="text-gray-600"> / </span>
                <span class="text-red-400">{{ tier.count - tier.wins }}</span>
              </span>
            </div>
            <div class="flex justify-between text-[10px]">
              <span class="text-gray-400">P&L</span>
              <span class="font-bold text-[11px]" :class="tier.pnl >= 0 ? 'text-teal-400' : 'text-orange-400'">
                {{ tier.pnl >= 0 ? '+' : '' }}{{ formatCurrency(tier.pnl) }}
              </span>
            </div>
            <div class="mt-0.5">
              <div class="flex justify-between text-[9px] mb-0.5">
                <span class="text-gray-500">Win Rate</span>
                <span class="font-bold" :class="i === 0 ? 'text-teal-400' : i === 1 ? 'text-yellow-400' : 'text-orange-400'">
                  {{ tier.winRate.toFixed(0) }}%
                </span>
              </div>
              <div class="w-full bg-gray-700/50 rounded-full h-1">
                <div class="h-1 rounded-full transition-all duration-700"
                  :class="i === 0 ? 'bg-teal-500' : i === 1 ? 'bg-yellow-500' : 'bg-orange-500'"
                  :style="{ width: tier.winRate + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ROW 4: TABS -->
      <div class="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <!-- Tab buttons -->
        <div class="grid grid-cols-7 border-b border-gray-800">
          <button @click="activeTab = 'active'"
            class="py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-0.5"
            :class="activeTab === 'active' ? 'text-cyan-400 bg-cyan-500/10 border-b-2 border-cyan-400' : 'text-gray-500 hover:text-gray-300'">
            &#9201; Ativas
            <span v-if="activeTrades.length" class="bg-cyan-500/20 text-cyan-400 text-[9px] px-1 rounded-full">{{ activeTrades.length }}</span>
          </button>
          <button @click="activeTab = 'pending'"
            class="py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-0.5"
            :class="activeTab === 'pending' ? 'text-blue-400 bg-blue-500/10 border-b-2 border-blue-400' : 'text-gray-500 hover:text-gray-300'">
            &#9203; Aguard.
            <span v-if="pendingTrades.length" class="bg-blue-500/20 text-blue-400 text-[9px] px-1 rounded-full">{{ pendingTrades.length }}</span>
          </button>
          <button @click="activeTab = 'results'"
            class="py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-0.5"
            :class="activeTab === 'results' ? 'text-purple-400 bg-purple-500/10 border-b-2 border-purple-400' : 'text-gray-500 hover:text-gray-300'">
            &#127942; Result.
            <span v-if="resolvedTrades.length" class="bg-purple-500/20 text-purple-400 text-[9px] px-1 rounded-full">{{ resolvedTrades.length }}</span>
          </button>
          <button @click="activeTab = 'stoploss'"
            class="py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-0.5"
            :class="activeTab === 'stoploss' ? 'text-red-400 bg-red-500/10 border-b-2 border-red-400' : 'text-gray-500 hover:text-gray-300'">
            &#128721; SL
            <span v-if="stopLossTrades.length" class="bg-red-500/20 text-red-400 text-[9px] px-1 rounded-full">{{ stopLossTrades.length }}</span>
          </button>
          <button @click="activeTab = 'claims'"
            class="py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-0.5"
            :class="activeTab === 'claims' ? 'text-teal-400 bg-teal-500/10 border-b-2 border-teal-400' : 'text-gray-500 hover:text-gray-300'">
            &#128176; Claims
            <span v-if="claimTrades.length" class="bg-teal-500/20 text-teal-400 text-[9px] px-1 rounded-full">{{ claimTrades.length }}</span>
          </button>
          <button @click="activeTab = 'hero'"
            class="py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-0.5"
            :class="activeTab === 'hero' ? 'text-yellow-400 bg-yellow-500/10 border-b-2 border-yellow-400' : 'text-gray-500 hover:text-gray-300'">
            &#127775; Hero
            <span v-if="heroTrades.length" class="bg-yellow-500/20 text-yellow-400 text-[9px] px-1 rounded-full">{{ heroTrades.length }}</span>
          </button>
          <button @click="activeTab = 'forecasts'"
            class="py-2 text-[10px] font-medium transition-all flex items-center justify-center gap-0.5"
            :class="activeTab === 'forecasts' ? 'text-sky-400 bg-sky-500/10 border-b-2 border-sky-400' : 'text-gray-500 hover:text-gray-300'">
            &#127780;&#65039; Prev.
            <span v-if="forecasts.length" class="bg-sky-500/20 text-sky-400 text-[9px] px-1 rounded-full">{{ forecasts.length }}</span>
          </button>
        </div>

        <!-- TAB: Ativas -->
        <div v-if="activeTab === 'active'" class="max-h-[420px] overflow-y-auto custom-scrollbar">
          <div v-if="activeTrades.length" class="px-3 py-1.5 bg-gray-800/30 flex justify-between text-[10px] text-gray-500">
            <span>{{ activeTrades.length }} ativas</span>
            <span>Investido: <b class="text-cyan-400">{{ formatCurrency(totalInvestedActive) }}</b></span>
          </div>
          <div v-if="activeTrades.length === 0" class="px-4 py-8 text-center text-gray-500 text-xs">Nenhuma aposta ativa</div>
          <div v-for="trade in paginatedActive" :key="trade.id"
            class="px-3 py-2.5 border-b border-gray-800/30 last:border-0 hover:bg-gray-800/30 transition"
            :class="{
              'border-l-4 !border-l-red-500': getSecondsLeft(trade.end_date) < 60,
              'border-l-4 !border-l-orange-500': getSecondsLeft(trade.end_date) >= 60 && getSecondsLeft(trade.end_date) < 300,
              'border-l-4 !border-l-yellow-500/50': getSecondsLeft(trade.end_date) >= 300 && getSecondsLeft(trade.end_date) < 1800,
            }">
            <div class="flex items-start gap-2">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center text-lg shrink-0 bg-cyan-500/20">
                <span>&#9729;&#65039;</span>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5 mb-0.5 flex-wrap">
                  <span v-if="trade.city" class="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-sky-500/20 text-sky-400">
                    {{ trade.city }}
                  </span>
                  <span v-if="trade.selected_outcome" class="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                    :class="(trade.selected_outcome||'').toUpperCase() === 'YES' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'">
                    {{ trade.selected_outcome }}
                  </span>
                  <span v-if="trade.edge_percent != null" class="text-[9px] px-1 rounded"
                    :class="(trade.edge_percent || 0) >= 10 ? 'bg-teal-500/20 text-teal-400' : 'bg-yellow-500/20 text-yellow-400'">
                    Edge {{ (trade.edge_percent || 0).toFixed(1) }}%
                  </span>
                  <span class="text-[8px] px-1 py-0.5 rounded font-bold"
                    :class="getRiskClass(trade.risk_level)">
                    {{ trade.risk_level || 'Moderate' }}
                  </span>
                </div>
                <p class="text-[11px] font-medium text-white truncate">{{ trade.question || trade.market_id || '--' }}</p>
                <div class="flex items-center gap-2 mt-0.5">
                  <span class="text-[9px] text-gray-500">{{ trade.weather_type || '--' }}</span>
                  <span class="text-[9px] text-gray-600">|</span>
                  <span class="text-[9px] text-gray-500">Custo: {{ formatCurrency(trade.cost_usdc || 0) }}</span>
                  <span v-if="trade.confidence" class="text-[9px] px-1 rounded" :class="getConfidenceColor(trade.confidence)">{{ trade.confidence }}%</span>
                </div>
              </div>
              <div class="text-right shrink-0">
                <p class="text-[11px] font-bold text-white">{{ formatCurrency(trade.cost_usdc || 0) }}</p>
                <p v-if="trade.profit_usdc != null" class="text-[10px] font-bold"
                  :class="(trade.profit_usdc || 0) >= 0 ? 'text-teal-400' : 'text-orange-400'">
                  {{ (trade.profit_usdc || 0) >= 0 ? '+' : '' }}{{ formatCurrency(trade.profit_usdc || 0) }}
                </p>
                <p class="font-mono text-[11px] font-bold" :class="getCountdownColor(trade.end_date)">
                  {{ formatCountdown(trade.end_date) }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="totalActivePages > 1" class="px-3 py-2 border-t border-gray-800 flex justify-center gap-1">
            <button v-for="p in totalActivePages" :key="p" @click="activeTradesPage = p"
              class="w-6 h-6 rounded text-[10px] transition"
              :class="activeTradesPage === p ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-500 hover:text-white'">{{ p }}</button>
          </div>
        </div>

        <!-- TAB: Aguardando -->
        <div v-if="activeTab === 'pending'" class="max-h-[420px] overflow-y-auto custom-scrollbar">
          <div v-if="pendingTrades.length === 0" class="px-4 py-8 text-center text-gray-500 text-xs">Nenhuma ordem pendente</div>
          <div v-for="trade in paginatedPending" :key="trade.id"
            class="px-3 py-2.5 border-b border-gray-800/30 last:border-0 hover:bg-gray-800/30 transition border-l-2 border-l-blue-500/40">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-500/20 shrink-0">
                <span class="text-blue-400 text-sm">&#9203;</span>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5 mb-0.5">
                  <span v-if="trade.city" class="text-[9px] px-1.5 py-0.5 rounded-full font-bold bg-sky-500/20 text-sky-400">{{ trade.city }}</span>
                  <span v-if="trade.selected_outcome" class="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                    :class="(trade.selected_outcome||'').toUpperCase() === 'YES' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'">
                    {{ trade.selected_outcome }}
                  </span>
                </div>
                <p class="text-[11px] font-medium text-white truncate">{{ trade.question || trade.market_id || '--' }}</p>
                <span class="text-[9px] text-gray-500">{{ formatDate(trade.timestamp) }}</span>
              </div>
              <div class="text-right shrink-0">
                <p class="text-blue-400 font-bold text-xs">{{ formatCurrency(trade.cost_usdc || 0) }}</p>
                <p class="text-[9px] text-gray-500">Aguardando</p>
              </div>
            </div>
          </div>
          <div v-if="totalPendingPages > 1" class="px-3 py-2 border-t border-gray-800 flex justify-center gap-1">
            <button v-for="p in totalPendingPages" :key="p" @click="pendingTradesPage = p"
              class="w-6 h-6 rounded text-[10px] transition"
              :class="pendingTradesPage === p ? 'bg-blue-500/20 text-blue-400' : 'text-gray-500 hover:text-white'">{{ p }}</button>
          </div>
        </div>

        <!-- TAB: Resultados -->
        <div v-if="activeTab === 'results'" class="max-h-[420px] overflow-y-auto custom-scrollbar">
          <div v-if="resolvedTrades.length === 0" class="px-4 py-8 text-center text-gray-500 text-xs">Nenhum resultado</div>
          <div v-for="trade in paginatedResults" :key="trade.id"
            class="px-3 py-2.5 border-b border-gray-800/30 last:border-0 hover:bg-gray-800/30 transition"
            :class="{ 'border-l-4 !border-l-green-500': trade.result === 'WIN', 'border-l-4 !border-l-red-500': trade.result === 'LOSS' }">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center text-lg shrink-0"
                :class="trade.result === 'WIN' ? 'bg-green-500/20' : 'bg-red-500/20'">
                <span v-if="trade.result === 'WIN'">&#127942;</span><span v-else>&#128128;</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[11px] font-medium leading-snug"
                  :class="trade.result === 'WIN' ? 'text-green-300' : 'text-red-300'">
                  {{ trade.question || trade.market_id || '--' }}
                </p>
                <div class="flex items-center gap-1.5 mt-0.5">
                  <span class="text-[9px] text-gray-500">{{ formatDate(trade.resolved_at || trade.timestamp) }}</span>
                  <span v-if="trade.city" class="text-[9px] px-1 py-0.5 rounded bg-sky-500/10 text-sky-400">{{ trade.city }}</span>
                  <span v-if="trade.edge_percent != null" class="text-[9px] text-gray-500">Edge {{ (trade.edge_percent || 0).toFixed(1) }}%</span>
                </div>
              </div>
              <div class="text-right shrink-0">
                <p v-if="trade.result === 'WIN'" class="text-green-400 font-bold text-xs">+{{ formatCurrency(trade.profit_usdc || 0) }}</p>
                <p v-else class="text-red-400 font-bold text-xs">{{ formatCurrency(trade.profit_usdc != null ? trade.profit_usdc : -(trade.cost_usdc || 0)) }}</p>
                <p class="text-[9px] font-bold" :class="trade.result === 'WIN' ? 'text-green-500' : 'text-red-500'">
                  {{ trade.result === 'WIN' ? '+' + (trade.profit_pct || 0).toFixed(0) + '%' : (trade.profit_usdc != null && trade.cost_usdc ? ((trade.profit_usdc / trade.cost_usdc) * 100).toFixed(0) + '%' : '-100%') }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="totalResultPages > 1" class="px-3 py-2 border-t border-gray-800 flex justify-center gap-1">
            <button v-for="p in totalResultPages" :key="p" @click="resultsPage = p"
              class="w-6 h-6 rounded text-[10px] transition"
              :class="resultsPage === p ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-white'">{{ p }}</button>
          </div>
        </div>

        <!-- TAB: Stop Loss -->
        <div v-if="activeTab === 'stoploss'" class="max-h-[420px] overflow-y-auto custom-scrollbar">
          <div v-if="stopLossTrades.length" class="px-3 py-2 bg-red-500/5 flex justify-between text-[10px] border-b border-gray-800/30">
            <span class="text-red-300">{{ stopLossTrades.length }} stop losses</span>
            <span>Perdido: <b class="text-red-400">{{ formatCurrency(totalStopLossLost) }}</b></span>
          </div>
          <div v-if="stopLossTrades.length === 0" class="px-4 py-8 text-center text-gray-500 text-xs">Nenhum stop loss acionado</div>
          <div v-for="trade in paginatedStopLoss" :key="trade.id"
            class="px-3 py-2.5 border-b border-gray-800/30 last:border-0 hover:bg-red-500/5 transition border-l-4 border-l-red-500">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center bg-red-500/20 shrink-0">
                <span class="text-red-400 text-sm">&#128721;</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[11px] font-medium text-red-300 leading-snug">{{ trade.question || trade.market_id || '--' }}</p>
                <div class="flex items-center gap-1.5 mt-0.5">
                  <span class="text-[9px] text-gray-500">{{ formatDate(trade.resolved_at || trade.timestamp) }}</span>
                  <span v-if="trade.city" class="text-[9px] px-1 py-0.5 rounded bg-sky-500/10 text-sky-400">{{ trade.city }}</span>
                </div>
              </div>
              <div class="text-right shrink-0">
                <p class="text-red-400 font-bold text-xs">{{ formatCurrency(trade.profit_usdc || 0) }}</p>
                <p class="text-[9px] text-yellow-400">
                  Salvou {{ formatCurrency(Math.abs(trade.profit_usdc || 0) > 0 ? (trade.cost_usdc || 0) + (trade.profit_usdc || 0) : 0) }}
                </p>
              </div>
            </div>
          </div>
          <div v-if="totalStopLossPages > 1" class="px-3 py-2 border-t border-gray-800 flex justify-center gap-1">
            <button v-for="p in totalStopLossPages" :key="p" @click="stopLossPage = p"
              class="w-6 h-6 rounded text-[10px] transition"
              :class="stopLossPage === p ? 'bg-red-500/20 text-red-400' : 'text-gray-500 hover:text-white'">{{ p }}</button>
          </div>
        </div>

        <!-- TAB: Claims -->
        <div v-if="activeTab === 'claims'" class="max-h-[420px] overflow-y-auto custom-scrollbar">
          <div v-if="claimTrades.length" class="px-3 py-1.5 bg-gray-800/30 flex justify-between text-[10px] border-b border-gray-800/30">
            <span class="text-teal-300">{{ claimTrades.length }} resgates</span>
            <span>Total: <b class="text-teal-400">+{{ formatCurrency(totalClaimProfit) }}</b></span>
          </div>
          <div v-if="claimTrades.length === 0" class="px-4 py-8 text-center text-gray-500 text-xs">Nenhum resgate encontrado</div>
          <div v-for="trade in paginatedClaims" :key="trade.id"
            class="px-3 py-2.5 border-b border-gray-800/30 last:border-0 flex items-center gap-2 hover:bg-teal-500/5 transition border-l-2 border-l-teal-500/50">
            <div class="w-7 h-7 rounded-lg flex items-center justify-center bg-teal-500/20 shrink-0">
              <span class="text-sm">&#128176;</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-[11px] font-medium text-white truncate">{{ trade.question || trade.market_id || '--' }}</p>
              <div class="flex items-center gap-1.5 mt-0.5">
                <span class="text-[9px] text-gray-500">{{ formatDate(trade.resolved_at || trade.timestamp) }}</span>
                <span v-if="trade.city" class="text-[9px] px-1 py-0.5 rounded bg-sky-500/10 text-sky-400">{{ trade.city }}</span>
              </div>
            </div>
            <div class="text-right shrink-0">
              <span class="text-teal-400 font-bold text-xs">+{{ formatCurrency(trade.profit_usdc || 0) }}</span>
            </div>
          </div>
          <div v-if="totalClaimPages > 1" class="px-3 py-2 border-t border-gray-800 flex justify-center gap-1">
            <button v-for="p in totalClaimPages" :key="p" @click="claimsPage = p"
              class="w-6 h-6 rounded text-[10px] transition"
              :class="claimsPage === p ? 'bg-teal-500/20 text-teal-400' : 'text-gray-500 hover:text-white'">{{ p }}</button>
          </div>
        </div>

        <!-- TAB: Hero Checkpoint -->
        <div v-if="activeTab === 'hero'" class="max-h-[420px] overflow-y-auto custom-scrollbar">
          <div v-if="heroTrades.length" class="px-3 py-2 bg-yellow-500/5 flex justify-between text-[10px] border-b border-gray-800/30">
            <span class="text-yellow-300">{{ heroTrades.length }} hero checkpoints</span>
            <span>Lucro salvo: <b class="text-yellow-400">{{ formatCurrency(totalHeroProfit) }}</b></span>
          </div>
          <div v-if="heroTrades.length === 0" class="px-4 py-8 text-center text-gray-500 text-xs">Nenhum hero checkpoint acionado</div>
          <div v-for="trade in paginatedHero" :key="trade.id"
            class="px-3 py-2.5 border-b border-gray-800/30 last:border-0 hover:bg-yellow-500/5 transition border-l-4 border-l-yellow-500">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 rounded-lg flex items-center justify-center bg-yellow-500/20 shrink-0">
                <span class="text-yellow-400 text-sm">&#127775;</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-[11px] font-medium text-yellow-300 truncate">{{ trade.question || trade.market_id || '--' }}</p>
                <div class="flex items-center gap-1.5 mt-0.5">
                  <span class="text-[9px] text-gray-500">{{ formatDate(trade.resolved_at || trade.timestamp) }}</span>
                  <span v-if="trade.city" class="text-[9px] px-1 py-0.5 rounded bg-sky-500/10 text-sky-400">{{ trade.city }}</span>
                </div>
              </div>
              <div class="text-right shrink-0">
                <p class="text-yellow-400 font-bold text-xs">+{{ formatCurrency(trade.profit_usdc || 0) }}</p>
                <p class="text-[9px] text-gray-500">{{ formatCurrency(trade.cost_usdc || 0) }} inv.</p>
              </div>
            </div>
          </div>
          <div v-if="totalHeroPages > 1" class="px-3 py-2 border-t border-gray-800 flex justify-center gap-1">
            <button v-for="p in totalHeroPages" :key="p" @click="heroPage = p"
              class="w-6 h-6 rounded text-[10px] transition"
              :class="heroPage === p ? 'bg-yellow-500/20 text-yellow-400' : 'text-gray-500 hover:text-white'">{{ p }}</button>
          </div>
        </div>

        <!-- TAB: Previsoes -->
        <div v-if="activeTab === 'forecasts'" class="max-h-[420px] overflow-y-auto custom-scrollbar">
          <div class="px-3 py-1.5 bg-gray-800/30 flex items-center justify-between text-[10px] border-b border-gray-800/30">
            <span class="text-sky-300">{{ forecasts.length }} previsoes</span>
            <div class="flex items-center gap-2">
              <span v-if="forecastsLoading" class="text-gray-500 animate-pulse">Carregando...</span>
              <button @click="fetchForecasts" :disabled="forecastsLoading"
                class="text-[10px] text-gray-500 hover:text-gray-300 transition disabled:opacity-40">&#8635; Atualizar</button>
            </div>
          </div>
          <div v-if="forecasts.length === 0 && !forecastsLoading" class="px-4 py-8 text-center text-gray-500 text-xs">
            Nenhuma previsao disponivel
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-b border-gray-800 text-gray-500 uppercase tracking-wider">
                  <th class="px-4 py-2 text-left font-medium">Cidade</th>
                  <th class="px-3 py-2 text-center font-medium">NOAA (H/L)</th>
                  <th class="px-3 py-2 text-center font-medium">WeatherAPI (H/L)</th>
                  <th class="px-3 py-2 text-center font-medium">Confianca</th>
                  <th class="px-3 py-2 text-center font-medium">Acordo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="f in forecasts" :key="f.city"
                  class="border-b border-gray-800/50 hover:bg-gray-800/20 transition">
                  <td class="px-4 py-2.5">
                    <span class="font-medium text-white">{{ f.city }}</span>
                  </td>
                  <td class="px-3 py-2.5 text-center text-gray-300">
                    <span class="text-red-400">{{ f.noaa_high || '--' }}</span>
                    <span class="text-gray-600"> / </span>
                    <span class="text-blue-400">{{ f.noaa_low || '--' }}</span>
                    <span class="text-gray-600 text-[10px]"> &deg;F</span>
                  </td>
                  <td class="px-3 py-2.5 text-center text-gray-300">
                    <span class="text-red-400">{{ f.wapi_high || '--' }}</span>
                    <span class="text-gray-600"> / </span>
                    <span class="text-blue-400">{{ f.wapi_low || '--' }}</span>
                    <span class="text-gray-600 text-[10px]"> &deg;F</span>
                  </td>
                  <td class="px-3 py-2.5 text-center">
                    <span class="px-1.5 py-0.5 rounded text-[10px] font-bold"
                      :class="(f.confidence || 0) >= 80 ? 'bg-green-500/20 text-green-400'
                        : (f.confidence || 0) >= 60 ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-red-500/20 text-red-400'">
                      {{ f.confidence || 0 }}%
                    </span>
                  </td>
                  <td class="px-3 py-2.5 text-center">
                    <span v-if="f.sources_agree" class="text-green-400 text-[10px] font-bold">&#10003; Sim</span>
                    <span v-else class="text-orange-400 text-[10px] font-bold">&#10007; Nao</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { getApiBaseUrl, getWsUrl, getApiV1BaseUrl } from '../config/runtime';

export default {
  name: 'Weather',
  setup() {
    // ---- State ----
    const loading = ref(true);
    const moduleEnabled = ref(false);
    const config = ref({ budget_amount: 0, budget_pct: 0 });
    const trades = ref([]);
    const forecasts = ref([]);
    const forecastsLoading = ref(false);
    const activeTab = ref('active');
    const chartPeriod = ref('1d');
    const now = ref(Date.now());
    const pageSize = 10;
    const activeTradesPage = ref(1);
    const pendingTradesPage = ref(1);
    const resultsPage = ref(1);
    const stopLossPage = ref(1);
    const claimsPage = ref(1);
    const heroPage = ref(1);

    // Chart tooltip
    const chartTooltip = ref({ show: false, pct: 50, svgX: 0, svgY: 0, value: 0, date: '' });

    let tickInterval = null;
    let fetchInterval = null;
    let ws = null;

    // ---- Formatters ----
    const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
    const formatDate = (d) => {
      if (!d) return '-';
      return new Date(d).toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
    };

    // ---- Countdown ----
    const getSecondsLeft = (endDate) => {
      if (!endDate) return 999999;
      return Math.max(0, Math.floor((new Date(endDate).getTime() - now.value) / 1000));
    };
    const formatCountdown = (endDate) => {
      const secs = getSecondsLeft(endDate);
      if (secs <= 0) return '00:00';
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const s = secs % 60;
      if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
      if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };
    const getCountdownColor = (endDate) => {
      const secs = getSecondsLeft(endDate);
      if (secs <= 30) return 'text-red-500 animate-pulse font-black';
      if (secs <= 60) return 'text-red-500';
      if (secs <= 300) return 'text-orange-400';
      if (secs <= 1800) return 'text-yellow-400';
      return 'text-green-400';
    };

    // ---- Risk badge ----
    const getRiskClass = (level) => {
      const l = (level || '').toLowerCase();
      if (l === 'conservative' || l === 'conservador') return 'bg-teal-500/20 text-teal-400';
      if (l === 'aggressive' || l === 'agressivo') return 'bg-orange-500/20 text-orange-400';
      return 'bg-yellow-500/20 text-yellow-400';
    };

    // ---- Confidence color ----
    const getConfidenceColor = (c) => c >= 70 ? 'bg-teal-500/20 text-teal-400' : c >= 60 ? 'bg-cyan-500/20 text-cyan-400' : 'bg-violet-500/20 text-violet-400';

    // ---- Computed trades ----
    const activeTrades = computed(() =>
      trades.value.filter(t => {
        const r = (t.result || '').toUpperCase();
        return r === 'PENDING' || r === 'ACTIVE' || r === '' || r === 'STALE_SYNC';
      }).sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime())
    );
    const pendingTrades = computed(() =>
      trades.value.filter(t => {
        const r = (t.result || '').toUpperCase();
        return r === 'PENDING' || r === '';
      })
    );
    const resolvedTrades = computed(() =>
      trades.value.filter(t => {
        const r = (t.result || '').toUpperCase();
        return r === 'WIN' || r === 'LOSS';
      })
    );
    const stopLossTrades = computed(() =>
      trades.value.filter(t => (t.result || '').toUpperCase() === 'STOP_LOSS')
    );
    const claimTrades = computed(() =>
      trades.value.filter(t => {
        const r = (t.result || '').toUpperCase();
        return r === 'REDEEM' || r === 'CLAIM' || r === 'TAKE_PROFIT';
      })
    );
    const heroTrades = computed(() =>
      trades.value.filter(t => {
        const r = (t.result || '').toUpperCase();
        return r === 'HERO_CHECKPOINT' || (t.sell_reason && String(t.sell_reason).toUpperCase().includes('HERO'));
      })
    );

    const wins = computed(() => resolvedTrades.value.filter(t => (t.result || '').toUpperCase() === 'WIN').length);
    const losses = computed(() => resolvedTrades.value.filter(t => (t.result || '').toUpperCase() === 'LOSS').length);

    const totalInvestedActive = computed(() =>
      activeTrades.value.reduce((s, t) => s + (t.cost_usdc || 0), 0)
    );
    const totalPnl = computed(() =>
      resolvedTrades.value.reduce((s, t) => {
        // profit_usdc should be positive for WIN, negative for LOSS
        if (t.profit_usdc != null) return s + t.profit_usdc;
        // Fallback: WIN adds profit, LOSS subtracts cost
        if (t.result === 'WIN') return s + (t.payout_usdc || 0) - (t.cost_usdc || 0);
        return s - (t.cost_usdc || 0);
      }, 0)
    );
    const roi = computed(() => {
      const invested = resolvedTrades.value.reduce((s, t) => s + (t.cost_usdc || 0), 0);
      if (!invested) return 0;
      return (totalPnl.value / invested) * 100;
    });
    const winRate = computed(() => {
      const r = resolvedTrades.value;
      if (!r.length) return 0;
      return (wins.value / r.length) * 100;
    });

    const totalStopLossLost = computed(() =>
      stopLossTrades.value.reduce((s, t) => s + (t.cost_usdc || 0), 0)
    );
    const totalClaimProfit = computed(() =>
      claimTrades.value.reduce((s, t) => s + (t.profit_usdc || 0), 0)
    );
    const totalHeroProfit = computed(() =>
      heroTrades.value.reduce((s, t) => s + (t.profit_usdc || 0), 0)
    );

    // ---- Tier Stats (CONSERVADOR / MODERADO / AGRESSIVO) ----
    const tierStats = computed(() => {
      const all = resolvedTrades.value;
      const tiers = [
        { label: 'CONSERVADOR', filter: t => (t.confidence || 0) >= 70 },
        { label: 'MODERADO', filter: t => (t.confidence || 0) >= 60 && (t.confidence || 0) < 70 },
        { label: 'AGRESSIVO', filter: t => (t.confidence || 0) < 60 },
      ];
      return tiers.map(tier => {
        const ts = all.filter(tier.filter);
        const w = ts.filter(t => t.result === 'WIN').length;
        const pnl = ts.reduce((s, t) => {
          if (t.profit_usdc != null) return s + t.profit_usdc;
          return s + (t.result === 'WIN' ? ((t.payout_usdc || 0) - (t.cost_usdc || 0)) : -(t.cost_usdc || 0));
        }, 0);
        return {
          label: tier.label,
          count: ts.length,
          wins: w,
          pnl,
          winRate: ts.length > 0 ? (w / ts.length) * 100 : 0,
        };
      });
    });

    // ---- Agent / tier helpers ----
    const getTradeTier = (conf) => {
      const c = Number(conf || 0);
      if (c >= 70) return { name: 'Conservador', bg: 'bg-teal-500/20', text: 'text-teal-400' };
      if (c >= 60) return { name: 'Moderado', bg: 'bg-yellow-500/20', text: 'text-yellow-400' };
      return { name: 'Extremo', bg: 'bg-orange-500/20', text: 'text-orange-400' };
    };

    // ---- Agent Stats (ROW 3) ----
    const userAgents = ref([]);

    const loadUserAgents = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const r = await fetch(`${getApiV1BaseUrl()}/user/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (r.ok) {
          const data = await r.json();
          userAgents.value = (data.agents || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        }
      } catch { /* silent */ }
    };

    const agentStats = computed(() => {
      if (!userAgents.value.length) return [];
      const sorted = [...userAgents.value].sort((a, b) => b.min_confidence - a.min_confidence);
      const allTrades = resolvedTrades.value;

      const riskBuckets = (ts) => {
        const c = { count: 0, wins: 0, pnl: 0 };
        const m = { count: 0, wins: 0, pnl: 0 };
        const e = { count: 0, wins: 0, pnl: 0 };
        for (const t of ts) {
          const conf = Number(t.confidence || 0);
          const pnl = t.profit_usdc != null ? t.profit_usdc : (t.result === 'WIN' ? ((t.payout_usdc || 0) - (t.cost_usdc || 0)) : -(t.cost_usdc || 0));
          const b = conf >= 70 ? c : conf >= 60 ? m : e;
          b.count++; if (t.result === 'WIN') b.wins++; b.pnl += pnl;
        }
        const wr = b => b.count > 0 ? Math.round(b.wins / b.count * 100) : 0;
        return {
          conservador: { ...c, winRate: wr(c) },
          moderado: { ...m, winRate: wr(m) },
          extremo: { ...e, winRate: wr(e) },
        };
      };

      return sorted.map((agent, i) => {
        const minConf = agent.min_confidence;
        const maxConf = i === 0 ? Infinity : sorted[i - 1].min_confidence;
        const agentTrades = allTrades.filter(t => {
          const conf = Number(t.confidence || 0);
          return conf >= minConf && conf < maxConf;
        });
        const wins = agentTrades.filter(t => t.result === 'WIN').length;
        const losses = agentTrades.filter(t => t.result === 'LOSS').length;
        const pnl = agentTrades.reduce((sum, t) => {
          if (t.profit_usdc != null) return sum + t.profit_usdc;
          return sum + (t.result === 'WIN' ? ((t.payout_usdc || 0) - (t.cost_usdc || 0)) : -(t.cost_usdc || t.size || 0));
        }, 0);
        return {
          agent,
          count: agentTrades.length,
          wins, losses, pnl,
          winRate: agentTrades.length > 0 ? (wins / agentTrades.length) * 100 : 0,
          risks: riskBuckets(agentTrades),
        };
      });
    });

    // ---- Pagination ----
    const totalActivePages = computed(() => Math.max(1, Math.ceil(activeTrades.value.length / pageSize)));
    const totalPendingPages = computed(() => Math.max(1, Math.ceil(pendingTrades.value.length / pageSize)));
    const totalResultPages = computed(() => Math.max(1, Math.ceil(resolvedTrades.value.length / pageSize)));
    const totalStopLossPages = computed(() => Math.max(1, Math.ceil(stopLossTrades.value.length / pageSize)));
    const totalClaimPages = computed(() => Math.max(1, Math.ceil(claimTrades.value.length / pageSize)));
    const totalHeroPages = computed(() => Math.max(1, Math.ceil(heroTrades.value.length / pageSize)));

    const paginate = (arr, page) => {
      const s = (page.value - 1) * pageSize;
      return arr.value.slice(s, s + pageSize);
    };
    const paginatedActive = computed(() => paginate(activeTrades, activeTradesPage));
    const paginatedPending = computed(() => paginate(pendingTrades, pendingTradesPage));
    const paginatedResults = computed(() => paginate(resolvedTrades, resultsPage));
    const paginatedStopLoss = computed(() => paginate(stopLossTrades, stopLossPage));
    const paginatedClaims = computed(() => paginate(claimTrades, claimsPage));
    const paginatedHero = computed(() => paginate(heroTrades, heroPage));

    // ---- P&L Chart ----
    const CHART_W = 600;
    const CHART_H = 180;
    const CHART_PAD = 8;

    const chartBalances = computed(() => {
      const resolved = [...resolvedTrades.value].sort((a, b) =>
        new Date(a.resolved_at || a.timestamp).getTime() - new Date(b.resolved_at || b.timestamp).getTime()
      );
      if (resolved.length < 2) return [];

      // Filter by period
      const now = Date.now();
      const periodMs = { '1d': 86400000, '1w': 604800000, '1m': 2592000000 };
      const cutoff = now - (periodMs[chartPeriod.value] || 2592000000);
      const filtered = resolved.filter(t => new Date(t.resolved_at || t.timestamp).getTime() >= cutoff);
      if (filtered.length < 2) return resolved.length >= 2 ? buildRunning(resolved) : [];
      return buildRunning(filtered);
    });

    const buildRunning = (sorted) => {
      let running = 0;
      return sorted.map(t => {
        if (t.profit_usdc != null) {
          running += t.profit_usdc;
        } else if (t.result === 'WIN') {
          running += (t.payout_usdc || 0) - (t.cost_usdc || 0);
        } else {
          running -= (t.cost_usdc || 0);
        }
        return running;
      });
    };

    const chartTimestamps = computed(() => {
      const resolved = [...resolvedTrades.value].sort((a, b) =>
        new Date(a.resolved_at || a.timestamp).getTime() - new Date(b.resolved_at || b.timestamp).getTime()
      );
      const now = Date.now();
      const periodMs = { '1d': 86400000, '1w': 604800000, '1m': 2592000000 };
      const cutoff = now - (periodMs[chartPeriod.value] || 2592000000);
      const filtered = resolved.filter(t => new Date(t.resolved_at || t.timestamp).getTime() >= cutoff);
      const src = filtered.length >= 2 ? filtered : resolved;
      return src.map(t => t.resolved_at || t.timestamp);
    });

    const chartYMin = computed(() => {
      const b = chartBalances.value;
      if (!b.length) return 0;
      const min = Math.min(...b);
      const max = Math.max(...b);
      if (min === max) return min - 1;
      return min - (max - min) * 0.1;
    });
    const chartYMax = computed(() => {
      const b = chartBalances.value;
      if (!b.length) return 100;
      const min = Math.min(...b);
      const max = Math.max(...b);
      if (min === max) return max + 1;
      return max + (max - min) * 0.1;
    });
    const chartYMid = computed(() => (chartYMin.value + chartYMax.value) / 2);

    const chartPctChange = computed(() => {
      const b = chartBalances.value;
      if (b.length < 2) return 0;
      const first = b[0];
      const last = b[b.length - 1];
      if (!first) return 0;
      return ((last - first) / Math.abs(first)) * 100;
    });

    const chartColor = computed(() => {
      const b = chartBalances.value;
      if (!b.length) return '#14b8a6';
      return b[b.length - 1] >= 0 ? '#14b8a6' : '#f97316';
    });

    const chartCoords = computed(() => {
      const b = chartBalances.value;
      if (b.length < 2) return [];
      const minV = chartYMin.value;
      const maxV = chartYMax.value;
      const range = maxV - minV || 1;
      return b.map((val, i) => ({
        x: Math.round((i / (b.length - 1)) * CHART_W * 10) / 10,
        y: Math.round((CHART_PAD + ((maxV - val) / range) * (CHART_H - CHART_PAD * 2)) * 10) / 10,
      }));
    });

    const chartSmoothPath = computed(() => {
      const coords = chartCoords.value;
      if (coords.length < 2) return '';
      let d = `M ${coords[0].x},${coords[0].y}`;
      for (let i = 1; i < coords.length; i++) {
        const prev = coords[i - 1];
        const curr = coords[i];
        const cpx = (prev.x + curr.x) / 2;
        d += ` C ${cpx},${prev.y} ${cpx},${curr.y} ${curr.x},${curr.y}`;
      }
      return d;
    });

    const chartSmoothFillPath = computed(() => {
      const coords = chartCoords.value;
      if (coords.length < 2) return '';
      let d = `M ${coords[0].x},${CHART_H}`;
      d += ` L ${coords[0].x},${coords[0].y}`;
      for (let i = 1; i < coords.length; i++) {
        const prev = coords[i - 1];
        const curr = coords[i];
        const cpx = (prev.x + curr.x) / 2;
        d += ` C ${cpx},${prev.y} ${cpx},${curr.y} ${curr.x},${curr.y}`;
      }
      d += ` L ${coords[coords.length - 1].x},${CHART_H} Z`;
      return d;
    });

    const chartLastPoint = computed(() => {
      const pts = chartCoords.value;
      return pts.length ? pts[pts.length - 1] : null;
    });

    const chartXStart = computed(() => {
      const ts = chartTimestamps.value;
      return ts.length ? formatDate(ts[0]) : '';
    });
    const chartXEnd = computed(() => {
      const ts = chartTimestamps.value;
      return ts.length ? formatDate(ts[ts.length - 1]) : '';
    });
    const chartXMid = computed(() => {
      const ts = chartTimestamps.value;
      if (ts.length < 3) return '';
      return formatDate(ts[Math.floor(ts.length / 2)]);
    });

    const setChartPeriod = (p) => {
      chartPeriod.value = p;
    };

    function onChartHover(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      const coords = chartCoords.value;
      const balances = chartBalances.value;
      const timestamps = chartTimestamps.value;
      if (!coords.length) return;
      const idx = Math.max(0, Math.min(coords.length - 1, Math.round(pct * (coords.length - 1))));
      chartTooltip.value = {
        show: true,
        pct: pct * 100,
        svgX: coords[idx].x,
        svgY: coords[idx].y,
        value: balances[idx] || 0,
        date: timestamps[idx] ? formatDate(timestamps[idx]) : '',
      };
    }

    // ---- API helpers ----
    const getHeaders = () => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (process.env.VUE_APP_API_KEY) headers['X-API-Key'] = process.env.VUE_APP_API_KEY;
      return headers;
    };

    // ---- Data fetching ----
    const fetchStatus = async () => {
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/v1/weather/status`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          moduleEnabled.value = data.enabled !== false;
        }
      } catch (_) {}
    };

    const fetchConfig = async () => {
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/v1/weather/config`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          config.value = data || {};
        }
      } catch (_) {}
    };

    const fetchTrades = async () => {
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/v1/weather/trades?limit=200`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          trades.value = data.trades || data || [];
        }
      } catch (_) {}
    };

    const fetchForecasts = async () => {
      forecastsLoading.value = true;
      try {
        const base = getApiBaseUrl();
        const res = await fetch(`${base}/v1/weather/forecasts`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          forecasts.value = data.forecasts || data || [];
        }
      } catch (_) {
      } finally {
        forecastsLoading.value = false;
      }
    };

    const fetchAll = async () => {
      await Promise.allSettled([
        fetchStatus(),
        fetchConfig(),
        fetchTrades(),
        fetchForecasts(),
        loadUserAgents(),
      ]);
      loading.value = false;
    };

    // ---- WebSocket ----
    const connectWs = () => {
      try {
        ws = new WebSocket(getWsUrl());
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'weather:trade' || msg.type === 'weather:status') {
              fetchTrades();
              fetchStatus();
            }
          } catch (_) {}
        };
        ws.onclose = () => setTimeout(connectWs, 5000);
        ws.onerror = () => {};
      } catch (_) {}
    };

    // ---- Lifecycle ----
    onMounted(() => {
      fetchAll();
      tickInterval = setInterval(() => { now.value = Date.now(); }, 1000);
      fetchInterval = setInterval(() => {
        fetchTrades();
        fetchStatus();
      }, 10000);
      connectWs();
    });

    onUnmounted(() => {
      if (tickInterval) clearInterval(tickInterval);
      if (fetchInterval) clearInterval(fetchInterval);
      if (ws) { try { ws.close(); } catch (_) {} }
    });

    return {
      loading, moduleEnabled, config, trades, forecasts, forecastsLoading,
      activeTab, chartPeriod, now,
      activeTradesPage, pendingTradesPage, resultsPage, stopLossPage, claimsPage, heroPage,
      activeTrades, pendingTrades, resolvedTrades, stopLossTrades, claimTrades, heroTrades,
      wins, losses, totalInvestedActive, totalPnl, roi, winRate,
      totalStopLossLost, totalClaimProfit, totalHeroProfit,
      tierStats, agentStats, userAgents, getTradeTier,
      totalActivePages, totalPendingPages, totalResultPages, totalStopLossPages, totalClaimPages, totalHeroPages,
      paginatedActive, paginatedPending, paginatedResults, paginatedStopLoss, paginatedClaims, paginatedHero,
      chartBalances, chartTimestamps, chartYMin, chartYMax, chartYMid, chartPctChange,
      chartColor, chartSmoothPath, chartSmoothFillPath, chartLastPoint,
      chartXStart, chartXEnd, chartXMid,
      chartTooltip, onChartHover, setChartPeriod,
      formatCurrency, formatDate, formatCountdown, getSecondsLeft, getCountdownColor,
      getRiskClass, getConfidenceColor,
      fetchForecasts,
    };
  },
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(31,41,55,0.5);
  border-radius: 2px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(107,114,128,0.4);
  border-radius: 2px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(107,114,128,0.7);
}
@keyframes chart-pulse { 0%, 100% { opacity: 1; r: 3.5px; } 50% { opacity: 0.7; r: 4.5px; } }
.animate-chart-pulse { animation: chart-pulse 2s ease-in-out infinite; }
@keyframes chart-pulse-ring { 0% { opacity: 0.6; r: 5px; } 100% { opacity: 0; r: 10px; } }
.animate-chart-pulse-ring { animation: chart-pulse-ring 2s ease-out infinite; }
</style>
